import { useEffect } from 'react';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { WaitlistForm } from '@/components/site/WaitlistForm';
import { WaitlistCounter } from '@/components/site/WaitlistCounter';
import { AppShowcaseCard } from '@/components/site/AppShowcaseCard';
import { APPLICATIONS } from '@/lib/applications';
import { setPageMeta } from '@/lib/seo';
import { useT, useLang } from '@/lib/i18n';

export default function Applications() {
  const t = useT();
  const { lang } = useLang();
  useEffect(() => {
    setPageMeta({
      title: t('seo.applications_title'),
      description: t('seo.applications_description'),
      path: '/applications',
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang]);

  return (
    <div data-testid="page-applications">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-10">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="om-anim-halo absolute left-[10%] -top-24 h-[380px] w-[380px] rounded-full blur-3xl"
              style={{ background: 'radial-gradient(closest-side, rgba(16,185,129,0.24), transparent 70%)' }}
            />
            <div
              className="om-anim-halo absolute right-[8%] top-[10%] h-[420px] w-[420px] rounded-full blur-3xl om-d-300"
              style={{ background: 'radial-gradient(closest-side, rgba(249,115,22,0.22), transparent 70%)' }}
            />
          </div>

          <div className="om-container text-center">
            <span className="om-eyebrow">{t('applications_page.eyebrow')}</span>
            <h1 className="om-h1 mt-6 text-[42px] sm:text-[56px] lg:text-[64px] text-[var(--om-ink)]">
              {t('applications_page.title_l1')}<br />
              <span
                style={{
                  backgroundImage:
                    'linear-gradient(120deg, #10B981 0%, #8B5CF6 45%, #F43F5E 80%, #F97316 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('applications_page.title_l2')}
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-[var(--om-muted)]">
              {t('applications_page.subtitle')}
            </p>
          </div>
        </section>

        <section className="pb-24 pt-6">
          <div className="om-container">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="applications-grid">
              {APPLICATIONS.map((app) => (
                <AppShowcaseCard key={app.id} app={app} />
              ))}
            </div>
          </div>
        </section>

        <section className="pb-24 pt-6 md:pb-32">
          <div className="om-container">
            <div className="relative overflow-hidden rounded-[32px] border border-[var(--om-border)] bg-white p-10 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-14">
              <div
                aria-hidden
                className="pointer-events-none absolute -left-20 -top-20 h-[320px] w-[320px] rounded-full blur-3xl"
                style={{ background: 'radial-gradient(closest-side, rgba(139,92,246,0.28), transparent 70%)' }}
              />
              <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-7">
                  <span className="om-eyebrow">{t('applications_page.cta_eyebrow')}</span>
                  <h2 className="om-h1 mt-4 text-[30px] sm:text-[40px] text-[var(--om-ink)]">
                    {t('applications_page.cta_title_l1')}<br />{t('applications_page.cta_title_l2')}
                  </h2>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-[var(--om-muted)]">
                    {t('applications_page.cta_subtitle')}
                  </p>
                </div>
                <div className="lg:col-span-5">
                  <WaitlistForm source="applications" variant="cta" />
                  <div className="mt-4"><WaitlistCounter /></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
